/**
 * ScheduledReportsPage — `/reporting/scheduled`.
 *
 * Lists `ScheduledReport` rows + lets the operator pause/resume/run-now.
 * Wired against the four endpoints in
 * `service-reporting-api/.../scheduled-report.controller.ts`.
 */
import { type JSX, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, Pause, Play, Plus, RotateCcw } from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import {
  createScheduledReport,
  listScheduledReports,
  pauseScheduledReport,
  resumeScheduledReport,
  runScheduledReportNow,
  type ScheduledReportWire,
} from '../../lib/api/reporting';
import { DeferredNotice, PageHeader, StatusBadge } from '../shared/StaffUI';

function todayIso(): string { return new Date().toISOString().slice(0, 10); }
function thirtyDaysAgoIso(): string {
  const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10);
}

export function ScheduledReportsPage(): JSX.Element {
  const qc = useQueryClient();
  const [showWizard, setShowWizard] = useState(false);
  const [reportKey, setReportKey] = useState('DAILY_REVENUE_BY_REGION');
  const [cron, setCron] = useState('0 6 * * 1');
  const [recipients, setRecipients] = useState('');
  const [wizardError, setWizardError] = useState<string | null>(null);

  const schedules = useQuery<readonly ScheduledReportWire[], Error>({
    queryKey: ['reporting', 'schedules'],
    queryFn: () => listScheduledReports(),
    retry: false,
  });

  const pauseM  = useMutation({ mutationFn: pauseScheduledReport,  onSuccess: () => void qc.invalidateQueries({ queryKey: ['reporting', 'schedules'] }) });
  const resumeM = useMutation({ mutationFn: resumeScheduledReport, onSuccess: () => void qc.invalidateQueries({ queryKey: ['reporting', 'schedules'] }) });
  const runM    = useMutation({ mutationFn: runScheduledReportNow, onSuccess: () => void qc.invalidateQueries({ queryKey: ['reporting', 'schedules'] }) });
  const createM = useMutation({
    mutationFn: () =>
      createScheduledReport({
        reportKey,
        params: { fromDate: thirtyDaysAgoIso(), toDate: todayIso() },
        cron,
        recipients: recipients.split(',').map((s) => s.trim()).filter(Boolean),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['reporting', 'schedules'] });
      void qc.invalidateQueries({ queryKey: ['reporting', 'dashboard'] });
      setShowWizard(false);
      setRecipients('');
      setWizardError(null);
    },
    onError: (err) => setWizardError(err instanceof Error ? err.message : 'Failed to create schedule'),
  });

  function submitWizard(): void {
    const list = recipients.split(',').map((s) => s.trim()).filter(Boolean);
    if (list.length === 0) {
      setWizardError('At least one recipient email required');
      return;
    }
    createM.mutate();
  }

  const denied =
    schedules.isError && schedules.error instanceof ApiError &&
    (schedules.error.status === 401 || schedules.error.status === 403);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Scheduled reports"
        description="Cron-triggered report runs with email distribution."
        actions={
          <button
            type="button"
            onClick={() => setShowWizard((v) => !v)}
            data-testid="new-schedule-btn"
            className="inline-flex items-center gap-2 px-3 py-2 text-xs font-medium text-white bg-nipost-green rounded-lg hover:bg-nipost-green/90 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" /> {showWizard ? 'Close' : 'New schedule'}
          </button>
        }
      />

      {showWizard && (
        <div className="bg-white border border-border rounded-xl p-5" data-testid="schedule-wizard">
          <h3 className="font-semibold text-gray-900 mb-3">New scheduled report</h3>
          {wizardError && (
            <p className="text-xs text-red-600 mb-3">{wizardError}</p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-1">
              <label className="text-[10px] font-semibold text-muted uppercase tracking-widest mb-1 block">Report key</label>
              <input
                type="text"
                value={reportKey}
                onChange={(e) => setReportKey(e.target.value)}
                aria-label="Report key"
                data-testid="schedule-report-key"
                className="w-full px-3 py-2 text-sm font-mono border border-border rounded-lg"
              />
            </div>
            <div className="md:col-span-1">
              <label className="text-[10px] font-semibold text-muted uppercase tracking-widest mb-1 block">Cron</label>
              <input
                type="text"
                value={cron}
                onChange={(e) => setCron(e.target.value)}
                aria-label="Cron"
                data-testid="schedule-cron"
                className="w-full px-3 py-2 text-sm font-mono border border-border rounded-lg"
              />
            </div>
            <div className="md:col-span-1">
              <label className="text-[10px] font-semibold text-muted uppercase tracking-widest mb-1 block">Recipients (csv)</label>
              <input
                type="text"
                value={recipients}
                onChange={(e) => setRecipients(e.target.value)}
                placeholder="ops@nipost.gov.ng"
                aria-label="Recipients"
                data-testid="schedule-recipients"
                className="w-full px-3 py-2 text-sm border border-border rounded-lg"
              />
            </div>
          </div>
          <button
            type="button"
            onClick={submitWizard}
            disabled={createM.isPending}
            data-testid="schedule-create-btn"
            className="mt-3 inline-flex items-center gap-1 px-3 py-2 text-xs font-medium text-white bg-nipost-green rounded-lg hover:bg-nipost-green/90 transition-colors disabled:opacity-50"
          >
            <Plus className="w-3.5 h-3.5" />
            {createM.isPending ? 'Creating…' : 'Create schedule'}
          </button>
        </div>
      )}

      {schedules.isPending ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : denied ? (
        <DeferredNotice title="Permission required">
          <code className="font-mono">Reporting/listSchedules</code> required.
        </DeferredNotice>
      ) : schedules.isError ? (
        <DeferredNotice title="Service temporarily unavailable">
          {schedules.error.message}
        </DeferredNotice>
      ) : !schedules.data || schedules.data.length === 0 ? (
        <p className="text-sm text-muted">No scheduled reports.</p>
      ) : (
        <div className="bg-white border border-border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
              <tr>
                <th className="text-left px-4 py-2">Report</th>
                <th className="text-left px-4 py-2">Cron</th>
                <th className="text-left px-4 py-2">Recipients</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-right px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {schedules.data.map((s) => (
                <tr key={s.id}>
                  <td className="px-4 py-2 font-mono text-xs">{s.reportKey}</td>
                  <td className="px-4 py-2 font-mono text-xs">{s.cron}</td>
                  <td className="px-4 py-2 text-xs">
                    <CalendarClock className="inline w-3 h-3 mr-1 text-muted" />
                    {s.recipients.length}
                  </td>
                  <td className="px-4 py-2">
                    <StatusBadge status={s.active ? 'ACTIVE' : 'PENDING'} />
                  </td>
                  <td className="px-4 py-2 text-right">
                    <div className="inline-flex items-center gap-1">
                      {s.active ? (
                        <button
                          type="button"
                          onClick={() => pauseM.mutate(s.id)}
                          className="px-2 py-1 text-xs text-amber-700 hover:bg-amber-50 rounded inline-flex items-center gap-1"
                        >
                          <Pause className="w-3 h-3" /> Pause
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => resumeM.mutate(s.id)}
                          className="px-2 py-1 text-xs text-nipost-green hover:bg-nipost-green-light rounded inline-flex items-center gap-1"
                        >
                          <Play className="w-3 h-3" /> Resume
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => runM.mutate(s.id)}
                        className="px-2 py-1 text-xs text-nipost-green hover:bg-nipost-green-light rounded inline-flex items-center gap-1"
                      >
                        <RotateCcw className="w-3 h-3" /> Run now
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
